import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { db, auth } from "@/lib/firebase";
import { updateDoc, doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    console.log('📧 VerifyEmail page loaded');
    console.log('📧 Current URL:', window.location.href);
    console.log('📧 Location pathname:', location.pathname);
    console.log('📧 Search params:', location.search);
    console.log('📧 Token from params:', token);
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    console.log('📧 Starting email verification...');
    
    if (!token) {
      console.error('📧 No token provided');
      setStatus("error");
      setMessage("Invalid verification link. No token provided.");
      return;
    }

    try {
      console.log('📧 Auth state:', auth.currentUser ? `Logged in as ${auth.currentUser.uid}` : 'Not logged in');

      // Look up verification token in email_verifications collection (public read access)
      console.log('📧 Looking up verification token:', token.substring(0, 10) + '...');

      const verificationRef = doc(db, "email_verifications", token);
      const verificationDoc = await getDoc(verificationRef);

      if (!verificationDoc.exists()) {
        console.error('📧 No verification record found');
        setStatus("error");
        setMessage("Invalid or expired verification link. This link may have already been used.");
        return;
      }

      const verificationData = verificationDoc.data();
      console.log('📧 Found verification record for user:', verificationData.userId);

      // Check if already verified
      if (verificationData.verified === true) {
        console.log('📧 Email already verified');
        setStatus("success");
        setMessage("Your email has already been verified! Please sign in to continue.");
        return;
      }

      // Check if token has expired
      if (verificationData.expiresAt) {
        const expiresAt = verificationData.expiresAt.toDate();
        console.log('📧 Token expires at:', expiresAt);
        if (expiresAt < new Date()) {
          console.error('📧 Token has expired');
          setStatus("error");
          setMessage("This verification link has expired. Please request a new verification email.");
          return;
        }
      }

      console.log('📧 Marking verification as complete...');

      // Mark verification record as verified
      await updateDoc(verificationRef, {
        verified: true,
        verifiedAt: new Date(),
      });

      // Update user document to mark as verified
      // This may fail if user is not authenticated, but that's okay
      // The verification record is the source of truth
      try {
        await updateDoc(doc(db, "users", verificationData.userId), {
          emailVerified: true,
        });
        console.log('📧 User document updated successfully');
      } catch (userUpdateError) {
        // This is expected if user is not authenticated
        // The login flow will check the verification record
        console.log('📧 Could not update user doc directly (expected if not authenticated)');
      }

      // Sign out the user if they're logged in (force fresh login with verified status)
      if (auth.currentUser) {
        console.log('📧 Signing out current user to force fresh login');
        await signOut(auth);
      }

      setStatus("success");
      setMessage("Your email has been verified successfully! Please sign in to your account to continue.");
      console.log('📧 Verification complete!');
    } catch (error: any) {
      console.error("📧 Error verifying email:", error);
      console.error("📧 Error code:", error.code);
      console.error("📧 Error message:", error.message);
      setStatus("error");
      setMessage(`An error occurred while verifying your email: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(220,27%,8%)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/icons/normiz-favicon.svg" alt="Normiz" className="h-16 w-16" />
          </div>
          <h1 className="text-3xl font-bold text-white">NORMIZ</h1>
          <p className="text-gray-400 mt-2">Email Verification</p>
        </div>

        <Card className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)]">
          <CardHeader>
            <CardTitle className="flex items-center justify-center gap-2 text-white">
              {status === "loading" && <Loader2 className="h-6 w-6 animate-spin" />}
              {status === "success" && <CheckCircle className="h-6 w-6 text-green-500" />}
              {status === "error" && <XCircle className="h-6 w-6 text-red-500" />}
              {status === "loading" && "Verifying..."}
              {status === "success" && "Email Verified!"}
              {status === "error" && "Verification Failed"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-gray-400">{message}</p>

            {status === "success" && (
              <Button onClick={() => window.location.href = "https://app.normiz.com/auth"} className="w-full bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]">
                Go to Sign In
              </Button>
            )}

            {status === "error" && (
              <div className="space-y-2">
                <Button onClick={() => navigate("/auth")} className="w-full bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]">
                  Go to Sign In
                </Button>
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="w-full border-[hsl(220,15%,25%)] bg-transparent text-gray-400 hover:bg-[hsl(220,15%,15%)] hover:text-white"
                >
                  Back to Home
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
